import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  IonApp,
  IonRouterOutlet
} from '@ionic/angular/standalone';
import { RouterModule, RouteReuseStrategy } from '@angular/router';
import { StorageService } from './services/storage.service';
import { IonicRouteStrategy } from '@ionic/angular';
import { LanguageService } from './services/language.service';
import { addIcons } from 'ionicons';
import { people, qrCode, share, add, checkmark } from 'ionicons/icons';
import { WebrtcMeshService } from './services/webrtc-mesh.service';
import { AndroidForegroundService } from './services/android-foreground.service';
import { IosBackgroundService } from './services/ios-background.service';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { ToastController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { BillingService } from './services/billing.service';
import { RevenueCatService } from './services/revenuecat.service';
import { DeviceService } from './services/device.service';
import { AudioService } from './services/audio.service';
import { AudioWakeLockService } from './services/audio-wake-lock.service';
import {
  markStartupHealthy,
  runStartupSelfHeal
} from './services/startup-debug.util';
import { UpdateAdvisorService } from './services/update-advisor.service';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IonApp,
    IonRouterOutlet
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // changeDetection: ChangeDetectionStrategy.OnPush, // Temporarily disabled for testing
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy }
  ],
  template: `
    <ion-app>
      <ion-router-outlet></ion-router-outlet>
    </ion-app>
  `,
  styles: [`
    ion-fab {
      margin-bottom: 60px; /* Add space above the tab bar */
    }
  `]
})
export class AppComponent implements OnInit {
  private readonly PENDING_JOIN_PAYLOAD_KEY = 'pending_join_payload';

  constructor(
    private storageService: StorageService,
    private languageService: LanguageService,
    private webrtcMeshService: WebrtcMeshService,
    private androidForeground: AndroidForegroundService,
    private iosBackground: IosBackgroundService,
    private toastController: ToastController,
    private router: Router,
    private billingService: BillingService,
    private revenueCatService: RevenueCatService,
    private deviceService: DeviceService,
    private audioService: AudioService,
    private audioWakeLockService: AudioWakeLockService,
    private updateAdvisor: UpdateAdvisorService
  ) {
    // Basic initialization only
    this.initializeApp();

    // Add needed icons
    addIcons({ people, qrCode, share, add, checkmark });

  }

  async ngOnInit(): Promise<void> {
    await this.checkAndroidVersion();
    await this.initializeRevenueCat();
    await this.billingService.refreshEntitlement();
    this.updateAdvisor.start();
    markStartupHealthy();
  }

  private async initializeRevenueCat(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();
      await this.revenueCatService.configure(deviceInfo.deviceId);

      this.revenueCatService.onCustomerInfoUpdated((info) => {
        this.billingService.handleRevenueCatUpdate(info);
      });
      await this.revenueCatService.startListeningForUpdates();
    } catch (error) {
      console.error('[App] RevenueCat initialization failed:', String(error));
    }
  }

  private async checkAndroidVersion(): Promise<void> {
    try {
      if (Capacitor.getPlatform() === 'android') {
        const info = await Device.getInfo();
        const androidVersion = parseInt(info.osVersion);

        if (androidVersion < 7) {
          const toast = await this.toastController.create({
            message: '⚠️ Android 7.0 or newer is recommended for best experience. Some features may not work properly on this device.',
            duration: 8000,
            position: 'top',
            color: 'warning',
            buttons: [
              {
                text: 'OK',
                role: 'cancel'
              }
            ]
          });
          await toast.present();
        }
      }
    } catch (error) {
      console.error('Could not check Android version:', String(error));
    }
  }

  private async initializeApp() {
    try {
      await runStartupSelfHeal();

      // Clean up legacy storage
      this.storageService.cleanupLegacyStorage();

      // Check for reconnection opportunities (non-blocking)
      this.checkForReconnection(); // Removed await to prevent blocking

      // Maintain connectivity when app goes to background/foreground
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          try {
            this.androidForeground.stopForegroundSession();
            if (!this.audioWakeLockService.isActive()) {
              this.iosBackground.stopBackgroundSession();
            }

            this.webrtcMeshService.setBackgroundState(false);
            this.audioService.resumeAudioContext();
          } catch { }
        } else {
          try {
            this.androidForeground.startForegroundSession('counting');
            this.iosBackground.startBackgroundSession('counting');

            this.webrtcMeshService.setBackgroundState(true);
          } catch { }
        }
      });

      // Handle deep links while app is running (native).
      App.addListener('appUrlOpen', ({ url }) => {
        this.handleIncomingJoinUrl(url);
      });

      // Handle deep links when app is launched from a URL.
      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        this.handleIncomingJoinUrl(launch.url);
      }

      // Handle PWA/browser links with join payload.
      this.handleIncomingJoinUrl(window.location.href);

    } catch (error: any) {
      console.error('Error during basic app initialization:', String(error));
    }
  }

  private handleIncomingJoinUrl(rawUrl: string): void {
    try {
      if (!rawUrl) return;
      const parsed = new URL(rawUrl);
      const payload = parsed.searchParams.get('payload') || parsed.searchParams.get('joinPayload');
      if (!payload) return;

      localStorage.setItem(this.PENDING_JOIN_PAYLOAD_KEY, payload);

      // Route user to stats where the join modal is opened.
      this.router.navigate(['/stats']).catch(() => {
        // Ignore navigation errors during app startup race conditions.
      });
    } catch (error) {
      console.error('[App] Failed to process deep link URL:', String(error));
    }
  }

  private async checkForReconnection(): Promise<void> {
    try {
      setTimeout(async () => {
        const reconnectionInfo = await this.webrtcMeshService.checkAndOfferReconnection();

        if (reconnectionInfo.shouldReconnect && reconnectionInfo.eventId) {
          const currentEventId = this.webrtcMeshService.getCurrentEventId();
          if (currentEventId === reconnectionInfo.eventId && this.webrtcMeshService.isInitialized()) {
            return;
          }

          await this.webrtcMeshService.reconnectToEvent(reconnectionInfo.eventId);
        }
      }, 2000);

    } catch (error: any) {
      console.error('[App] Error during reconnection check:', String(error));
    }
  }
}